import { useEffect, useState } from "react";
import { ChevronDown, Inbox, Loader2, MessageSquareQuote, Repeat, Star } from "lucide-react";
import { api } from "@/lib/api";
import { Card, Empty, Stars } from "@/components/common";
import { cn } from "@/lib/utils";

const TOOL_LABELS = {
  very_difficult: "Very difficult", somewhat_difficult: "Somewhat difficult", neutral: "Neutral", helpful: "Helpful", very_helpful: "Very helpful",
};
const EXPECT_LABELS = {
  fell_short: "Fell short", fell_slightly_short: "Fell slightly short", met: "Met expectations", exceeded: "Exceeded expectations",
};

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); } catch { return iso; }
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-400"><Icon className="h-4 w-4" /><span className="text-[11px] font-semibold uppercase tracking-wider">{label}</span></div>
      <p className="mt-2 font-display text-3xl font-bold text-navy-900">{value}</p>
    </Card>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 py-1.5">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-navy-900">{children}</span>
    </div>
  );
}

function TextRow({ label, value }) {
  return (
    <div className="py-1.5">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="mt-0.5 whitespace-pre-line text-sm text-navy-900">{value?.trim() ? value : <span className="text-slate-300">—</span>}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="border-t border-slate-100 pt-3">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{title}</p>
      {children}
    </div>
  );
}

function ResponseBanner({ r }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="overflow-hidden" data-testid={`survey-response-${r.id}`}>
      <button onClick={() => setOpen((v) => !v)} data-testid={`survey-response-header-${r.id}`} className="flex w-full items-center gap-3 p-4 text-left sm:px-5">
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="truncate font-display text-[15px] font-semibold text-navy-900">{r.clientName}</span>
            <Stars value={r.overallRating} />
          </span>
          <span className="mt-0.5 block truncate text-xs text-slate-400">{r.tripTitle}{r.bookingNumber ? ` · ${r.bookingNumber}` : ""} · {fmtDate(r.submittedAt)}</span>
        </span>
        {r.testimonial && <span className="hidden shrink-0 rounded-full bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-600 sm:inline">Testimonial OK</span>}
        <ChevronDown className={cn("h-4 w-4 shrink-0 text-slate-400 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div className="space-y-3 border-t border-slate-100 p-5 sm:px-5" data-testid={`survey-response-body-${r.id}`}>
          <Section title="Overall Experience">
            <Row label="Overall rating"><Stars value={r.overallRating} /></Row>
          </Section>
          <Section title="Booking Process">
            <Row label="Booking process satisfaction"><Stars value={r.bookingSatisfaction} /></Row>
            <Row label="Communication satisfaction"><Stars value={r.commSatisfaction} /></Row>
            <Row label="Felt supported">{r.feltSupported ? "Yes" : "No"}</Row>
            <TextRow label="Liked most about booking" value={r.likedMost} />
            <TextRow label="Frustrating or confusing" value={r.frustrating} />
          </Section>
          <Section title="Tools & Usability">
            <Row label="Invoices">{TOOL_LABELS[r.toolsInvoices] || "—"}</Row>
            <Row label="Trip Itinerary">{TOOL_LABELS[r.toolsItinerary] || "—"}</Row>
            <Row label="Making Payments">{TOOL_LABELS[r.toolsPayments] || "—"}</Row>
          </Section>
          <Section title="Trip Experience">
            <Row label="Met expectations">{EXPECT_LABELS[r.expectationsMet] || "—"}</Row>
            <Row label="Resort / Stateroom"><Stars value={r.aspectResort} /></Row>
            <Row label="Transportation / Ship"><Stars value={r.aspectTransportation} /></Row>
            <Row label="Activities / Excursions"><Stars value={r.aspectActivities} /></Row>
            <Row label="Dining / Beverages"><Stars value={r.aspectDining} /></Row>
            <Row label="Overall Value"><Stars value={r.aspectValue} /></Row>
            <TextRow label="Issues during the trip" value={r.tripIssues} />
          </Section>
          <Section title="Final Feedback">
            <Row label="Likelihood to recommend">{r.recommend}/10</Row>
            <Row label="Would book again">{r.bookAgain ? "Yes" : "No"}</Row>
            <Row label="May use as testimonial">{r.testimonial ? "Yes" : "No"}</Row>
            <TextRow label="What could we do better" value={r.improve} />
            <TextRow label="Anything else" value={r.anythingElse} />
          </Section>
        </div>
      )}
    </Card>
  );
}

export default function SurveysTab() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    api.get("/surveys").then(({ data }) => setData(data)).catch(() => { setFailed(true); setData({ responses: [], stats: { total: 0, avgOverall: 0, bookAgainPct: 0 } }); });
  }, []);

  if (!data) return <Card><div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-navy-700" /></div></Card>;
  if (failed) return <Card data-testid="communication-surveys-page"><Empty icon={MessageSquareQuote} title="Couldn't load survey responses" text="Something went wrong fetching the data. Please refresh the page and try again." /></Card>;
  const { responses, stats } = data;

  return (
    <div className="space-y-4" data-testid="communication-surveys-page">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat icon={Inbox} label="Total responses" value={stats.total} testid="survey-stat-total" />
        <Stat icon={Star} label="Avg overall rating" value={stats.total ? `${stats.avgOverall} / 5` : "—"} testid="survey-stat-overall" />
        <Stat icon={Repeat} label="Would book again" value={stats.total ? `${stats.bookAgainPct}%` : "—"} testid="survey-stat-bookagain" />
      </div>

      {responses.length === 0 ? (
        <Card><Empty icon={MessageSquareQuote} title="No survey responses yet" text="Responses from the Post-Trip Experience Survey will appear here as travelers complete them." /></Card>
      ) : (
        <div className="space-y-3">{responses.map((r) => <ResponseBanner key={r.id} r={r} />)}</div>
      )}
    </div>
  );
}
